import type { GraviGrid } from '@gravitate-js/excalibrr'
import type { UserSettingsHook } from '@hooks/settings'
import type { IRowNode, StatusPanelDef, ValueGetterParams } from 'ag-grid-community'
import dayjs from 'dayjs'
import type { ComponentProps } from 'react'
import { getLocalStorage, setLocalStorage } from '../general'

export type GridViewManager = NonNullable<ComponentProps<typeof GraviGrid>['gridViewManager']>
export type ViewPayload = Parameters<GridViewManager['saveView']>[0]
export type GridView = Awaited<ReturnType<GridViewManager['saveView']>>[number]

export const GRID_VIEW_LIST_KEY_PREFIX = 'gridViewList::'

/** Standard context menu items for analytics/reporting grids */
export const defaultContextMenuItems = [
  'copy',
  'copyWithHeaders',
  'paste',
  'separator',
  'export',
  'separator',
  'chartRange',
  'pivotChartAndPivotMode',
  'separator',
  'resetColumns',
  'expandAll',
  'contractAll',
] as const

/** Standard status bar panels for analytics/reporting grids */
export const defaultStatusPanels: StatusPanelDef[] = [
  { statusPanel: 'agTotalAndFilteredRowCountComponent' },
  { statusPanel: 'agTotalRowCountComponent' },
  { statusPanel: 'agFilteredRowCountComponent' },
  { statusPanel: 'agSelectedRowCountComponent' },
  { statusPanel: 'agAggregationComponent' },
]

export function getStoreNumberComparator<T>(key: string | ((data: T | undefined) => unknown)) {
  const read = (node: IRowNode<T>) => (typeof key === 'function' ? key(node?.data) : node?.data?.[key])
  return (_a: unknown, _b: unknown, nodeA: IRowNode<T>, nodeB: IRowNode<T>) => {
    // For grouped rows, we need to compare the group keys
    const valueA = nodeA?.group ? nodeA.key : read(nodeA)
    const valueB = nodeB?.group ? nodeB.key : read(nodeB)

    if (!valueA && !valueB) {
      return 0
    }
    if (!valueA) {
      return 1
    }
    if (!valueB) {
      return -1
    }
    return compareStoreNumbers(valueA, valueB)
  }
}
// Sort stores storeSort sortStore sortStores
export function compareStoreNumbers(a, b) {
  const isNumeric = (str) => !isNaN(str)

  if (a === 'N/A' || b === 'N/A') {
    return (a === 'N/A' ? 1 : 0) - (b === 'N/A' ? 1 : 0)
  }

  // Check if both are numeric
  if (isNumeric(a) && isNumeric(b)) {
    return Number(a) - Number(b)
  }

  // If 'a' is numeric and 'b' is not, 'a' comes first
  if (isNumeric(a)) {
    return -1
  }

  // If 'b' is numeric and 'a' is not, 'b' comes first
  if (isNumeric(b)) {
    return 1
  }

  // If neither is numeric, sort alphabetically
  return a.localeCompare(b)
}

export function getDefaultDateComparator(format?: string) {
  return (valueA: string, valueB: string, _: unknown, __: unknown) => {
    const dateA = dayjs(valueA, format)
    const dateB = dayjs(valueB, format)
    return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0
  }
}

/** For `agDateColumnFilter` on a string cell value, which AG Grid otherwise compares
 * against a Date and silently matches everything or nothing. */
export function dateFilterComparator(filterDate: Date, cellValue: string | null | undefined): number {
  if (!cellValue) return -1
  const cell = dayjs(cellValue).startOf('day')
  const filter = dayjs(filterDate).startOf('day')
  if (cell.isBefore(filter)) return -1
  if (cell.isAfter(filter)) return 1
  return 0
}

function findGridViewIndex(gridViewList: GridView[], payload: ViewPayload) {
  return gridViewList.findIndex((view) => (payload.id ? view.id === payload.id : view.name === payload.name))
}

export async function saveGridView(
  viewPayload: ViewPayload,
  storageKey: string,
  updateSetting: UserSettingsHook['updateSetting']
) {
  const key = `${GRID_VIEW_LIST_KEY_PREFIX}${storageKey}`
  // Get existing grid views
  const gridViewList = (getLocalStorage(key) || []) as GridView[]

  // Build new grid view
  const id = viewPayload.id || crypto.randomUUID()
  const newGridView = { ...viewPayload, id } as GridView

  // Update or insert
  const viewIndex = findGridViewIndex(gridViewList, viewPayload)
  const updatedViews =
    viewIndex === -1
      ? [...gridViewList, newGridView]
      : gridViewList.map((view, index) => (index === viewIndex ? newGridView : view))
  // Save to local storage and update setting
  try {
    const gridViewListString = JSON.stringify(updatedViews)
    await updateSetting(key, gridViewListString)
    setLocalStorage(key, gridViewListString)
  } catch (error) {
    console.error('Error saving grid view:', error)
  }
  return updatedViews
}

export function deleteGridView(
  gridView: ViewPayload,
  storageKey: string,
  updateSetting: UserSettingsHook['updateSetting']
) {
  const key = `${GRID_VIEW_LIST_KEY_PREFIX}${storageKey}`
  // Get existing grid views
  const gridViewList = (getLocalStorage(key) || []) as GridView[]

  // If found, delete
  const viewIndex = findGridViewIndex(gridViewList, gridView)
  if (viewIndex !== -1) {
    gridViewList.splice(viewIndex, 1)
  } else {
    console.error(`Unable to delete. Grid view with name (${gridView.name}) not found.`)
  }

  try {
    const gridViewListString = JSON.stringify(gridViewList)
    updateSetting(key, gridViewListString)
    setLocalStorage(key, gridViewListString)
  } catch (error) {
    console.error('Error deleting grid view:', error)
  }
  return gridViewList
}

export function gridViewManager(
  storageKey: string,
  updateSetting: UserSettingsHook['updateSetting']
): {
  gridViewManager: {
    saveView: (viewPayload: ViewPayload) => Promise<GridView[]>
    deleteView: (gridView: ViewPayload) => GridView[]
  }
} {
  return {
    gridViewManager: {
      saveView: async (viewPayload: ViewPayload) => {
        return await saveGridView(viewPayload, storageKey, updateSetting)
      },
      deleteView: (gridView: ViewPayload) => {
        return deleteGridView(gridView, storageKey, updateSetting)
      },
    },
  }
}

type ComparatorType = 'number' | 'string' | 'date' | 'time' | 'site' | 'number_string'

/** Value-based comparison — for columns whose sortable value is derived (e.g. via a valueGetter) rather than read straight off row data. */
export function compareValues(a: string, b: string, type: ComparatorType) {
  switch (type) {
    case 'number':
      return Number(a) - Number(b)
    case 'string':
      return String(a ?? '').localeCompare(String(b ?? ''))
    case 'number_string': {
      const numA = parseFloat(a)
      const numB = parseFloat(b)
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
      // callers can pass raw grid values which may not be strings — coerce so sorting never throws
      return String(a ?? '').localeCompare(String(b ?? ''))
    }
    case 'date':
      return dayjs(a).valueOf() - dayjs(b).valueOf()
    case 'site': {
      const siteA = a.split(' - ')[0]
      const siteB = b.split(' - ')[0]
      return Number(siteA) - Number(siteB)
    }
    case 'time': {
      const format = 'h:mm A z'
      return dayjs(a, format).valueOf() - dayjs(b, format).valueOf()
    }
  }
}

export function getComparator<T>(key: keyof T & string, type: ComparatorType) {
  return (_a: unknown, _b: unknown, nodeA: IRowNode<T>, nodeB: IRowNode<T>) => {
    const a = (nodeA?.group ? nodeA.key : nodeA?.data?.[key]) as string
    const b = (nodeB?.group ? nodeB.key : nodeB?.data?.[key]) as string
    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1
    return compareValues(a, b, type)
  }
}

export function getColumnFilterComparator(type: ComparatorType) {
  return (a: string | null, b: string | null): number => {
    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1
    return compareValues(a, b, type)
  }
}

const TIME_FILTER_OPTIONS = [
  'equals',
  'notEqual',
  'lessThan',
  'lessThanOrEqual',
  'greaterThan',
  'greaterThanOrEqual',
] as const

// Custom filter params for time columns e.g. BOL Time in Supplier Rec
// Enables less than, greater than, etc. filters for time values
export function getDefaultTimeFilterParams() {
  return {
    filterOptions: TIME_FILTER_OPTIONS,
    textCustomComparator: (filter: (typeof TIME_FILTER_OPTIONS)[number], value: string, filterText: string) => {
      const baseDate = '2000-01-01'
      const filterHasAMPM = /[AP]\.?M\.?/i.test(filterText)
      const valueHasAMPM = /[AP]\.?M\.?/i.test(value)
      const filterFormat = filterHasAMPM ? 'h:mm A' : 'H:mm'
      const valueFormat = valueHasAMPM ? 'h:mm A' : 'H:mm'
      const valueTime = dayjs(`${baseDate} ${value.toUpperCase()}`, `YYYY-MM-DD ${valueFormat}`).valueOf()
      const filterTime = dayjs(`${baseDate} ${filterText.toUpperCase()}`, `YYYY-MM-DD ${filterFormat}`).valueOf()

      const comparisons: Record<(typeof TIME_FILTER_OPTIONS)[number], boolean> = {
        equals: valueTime === filterTime,
        notEqual: valueTime !== filterTime,
        lessThan: valueTime < filterTime,
        lessThanOrEqual: valueTime <= filterTime,
        greaterThan: valueTime > filterTime,
        greaterThanOrEqual: valueTime >= filterTime,
      }
      return comparisons[filter] ?? false
    },
  }
}

export function getParentNodes(node: IRowNode | null | undefined) {
  const parents = []
  let current = node?.parent
  while (current) {
    parents.push(current)
    current = current.parent
  }
  return parents
}

export function getDateFilterValueGetter<T>(params: ValueGetterParams<T>, key: keyof T): Date | null {
  const data = params.data?.[key]
  const value = Array.isArray(data) ? data[0] : data
  if (!value) return null

  const date = new Date(value)
  date.setHours(0, 0, 0, 0)
  return date
}
